import { BLOCKING_ISSUES, ISSUE, NOTE, ROW_STATUS } from '../constants/reviewFilters.js';
import { DUPLICATE_KIND } from '../constants/inviteModes.js';
import { isOwnerWord } from './accessRules.js';
import { detectDuplicates } from './detectDuplicates.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SUGGESTION_DISTANCE = 2;

const lower = (value) => String(value ?? '').trim().toLowerCase();

/** Levenshtein distance, enough for "Devloper" -> "Developer". */
function distance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
}

/**
 * The catalog name closest to `name` (case-insensitive), or `null` when none is close.
 *
 * @param {string} name
 * @param {string[]} names
 */
export function suggestName(name, names) {
  const target = lower(name);
  let best = null;
  let bestDistance = Infinity;
  names.forEach((candidate) => {
    const d = distance(target, lower(candidate));
    if (d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  });
  return bestDistance <= MAX_SUGGESTION_DISTANCE ? best : null;
}

/** Emails of rows that sit on a reporting cycle (A -> B -> A), never the self-reports. */
function loopEmails(rows) {
  const managerOf = new Map(
    rows
      .filter((r) => EMAIL_RE.test(lower(r.person.email)) && lower(r.person.reports_to) !== lower(r.person.email))
      .map((r) => [lower(r.person.email), lower(r.person.reports_to)]),
  );
  const inLoop = new Set();
  managerOf.forEach((_, start) => {
    const path = [];
    let current = start;
    while (current && managerOf.has(current) && !path.includes(current)) {
      path.push(current);
      current = managerOf.get(current);
    }
    if (current === start) path.forEach((email) => inLoop.add(email));
  });
  return inLoop;
}

const fieldChanges = (person, member) => {
  const changes = [];
  const name = `${person.first_name} ${person.last_name}`.trim();
  if (name && lower(name) !== lower(member.name)) changes.push('name');
  if (person.job_title && person.job_title !== (member.job_title ?? '')) changes.push('job_title');
  if (person.location && person.location !== (member.location ?? '')) changes.push('location');
  return changes;
};

/** Row number of the first row of each email (rows with an email only). */
function firstRowByEmail(rows) {
  const first = new Map();
  [...rows]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .forEach((row) => {
      const email = lower(row.person.email);
      if (email && !first.has(email)) first.set(email, row.rowNumber);
    });
  return first;
}

/**
 * Derives `status`, `issues`, `notes` (informative lines under the status), `existingMemberId` and `changes` of every row. Pure: nothing is
 * stored on the rows besides what it returns. Blocking issues make the row an error; warnings
 * (outside your domain, possible duplicate) only feed filters and badges.
 *
 * @param {object[]} rows Review rows (see `buildRows`).
 * @param {{ domain: string, roles: Array<{ name: string }>, groups: Array<{ name: string }>, members: object[] }} catalog
 *   Contract TBD (ARP/backend, ARP-1480): matched by case-insensitive name/email until it exists.
 */
export function validateRows(rows, catalog) {
  const roleNames = catalog.roles.map((r) => r.name);
  const groupNames = catalog.groups.map((g) => g.name);
  const known = (names) => new Set(names.map(lower));
  const knownRoles = known(roleNames);
  const knownGroups = known(groupNames);
  const membersByEmail = new Map(catalog.members.map((m) => [lower(m.email), m]));
  const membersById = new Map(catalog.members.map((m) => [m.id, m]));
  const loops = loopEmails(rows);
  const duplicates = detectDuplicates(rows, catalog.members);
  const firstRowOfEmail = firstRowByEmail(rows);
  const fileEmails = new Set(rows.map((r) => lower(r.person.email)));

  return rows.map((row) => {
    const { person, accessFromFile } = row;
    const email = lower(person.email);
    const decidedMember =
      row.resolution === 'use_existing' ? membersById.get(row.existingMemberId) : null;
    const member = membersByEmail.get(email) ?? decidedMember;
    const issues = [];

    if (!EMAIL_RE.test(email)) issues.push({ code: ISSUE.INVALID_EMAIL });
    if (member?.user_type === 'owner' || isOwnerWord(accessFromFile.user_type)) issues.push({ code: ISSUE.OWNER_NOT_IMPORTABLE });
    accessFromFile.roles
      .filter((name) => !knownRoles.has(lower(name)))
      .forEach((value) =>
        issues.push({ code: ISSUE.UNKNOWN_ROLE, value, suggestion: suggestName(value, roleNames) }),
      );
    accessFromFile.groups
      .filter((name) => !knownGroups.has(lower(name)))
      .forEach((value) =>
        issues.push({ code: ISSUE.UNKNOWN_GROUP, value, suggestion: suggestName(value, groupNames) }),
      );
    if (person.reports_to && lower(person.reports_to) === email) {
      issues.push({ code: ISSUE.REPORTS_TO_SELF });
    }
    if (loops.has(email)) issues.push({ code: ISSUE.REPORTING_LOOP });
    // The host after the last `@` must be the workspace domain: `notavance.com` is not `avance.com`.
    if (EMAIL_RE.test(email) && email.slice(email.lastIndexOf('@') + 1) !== lower(catalog.domain)) {
      issues.push({ code: ISSUE.OUTSIDE_DOMAIN });
    }
    // The same email again in the file: decided in "Before you add them", never dropped (CT-5834).
    const firstRow = firstRowOfEmail.get(email);
    const sameEmail = firstRow !== undefined && firstRow !== row.rowNumber
      ? { kind: DUPLICATE_KIND.FILE, otherRowNumber: firstRow, sameEmail: true }
      : null;
    const duplicate = sameEmail ?? duplicates.get(row.rowNumber);
    if (duplicate && (sameEmail || !member) && !row.resolution) {
      issues.push(
        duplicate.kind === DUPLICATE_KIND.MEMBER
          ? { code: ISSUE.POSSIBLE_DUPLICATE, kind: DUPLICATE_KIND.MEMBER, memberId: duplicate.member.id }
          : {
              code: ISSUE.POSSIBLE_DUPLICATE,
              kind: DUPLICATE_KIND.FILE,
              otherRowNumber: duplicate.otherRowNumber,
              // The very same email twice is one person: only one of the rows can stay.
              ...(duplicate.sameEmail ? { sameEmail: true } : {}),
            },
      );
    }

    const blocked = issues.some((issue) => BLOCKING_ISSUES.includes(issue.code));
    const existing = member && member.user_type !== 'owner' ? member : null;
    const notes = [];
    if (existing?.status === 'suspended') notes.push({ code: NOTE.SUSPENDED_UPDATE });
    // A manager who is neither in the file nor in the workspace stays as the typed email.
    const manager = lower(person.reports_to);
    if (EMAIL_RE.test(manager) && manager !== email && !fileEmails.has(manager) && !membersByEmail.has(manager)) {
      notes.push({ code: NOTE.REPORTS_TO_UNKNOWN, email: person.reports_to.trim() });
    }
    let status = ROW_STATUS.NEW;
    if (blocked) status = ROW_STATUS.ERROR;
    else if (existing) status = ROW_STATUS.UPDATE;

    return {
      ...row,
      status,
      issues,
      notes,
      existingMemberId: existing?.id ?? null,
      changes: existing ? fieldChanges(person, existing) : [],
    };
  });
}

/**
 * Validates the rows of the file and sets aside the members with nothing to update. A repeated
 * email is NOT set aside: it stays listed and is flagged as a possible duplicate to decide in
 * "Before you add them" (Keep row N only: the same email is one person).
 *
 * @returns {{ listed: object[], notListed: { unchanged: number } }}
 */
export function partitionRows(rows, catalog) {
  const validated = validateRows(rows, catalog);
  const listed = validated.filter(
    (row) => !(row.status === ROW_STATUS.UPDATE && row.changes.length === 0),
  );
  return { listed, notListed: { unchanged: validated.length - listed.length } };
}
